import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { buyersApi, type BuyerLeadDetailDto } from "@/api/buyers";
import { buyerKeys, buyerAssignmentKeys, buyerAllocationKeys } from "@/hooks/buyers/buyerKeys";

/** The server's floor, mirrored so the button can say why it is disabled
 *  before a round trip. The server enforces it regardless. */
export const MIN_ARCHIVE_REASON = 10;

/**
 * Archive or unarchive one buyer.
 *
 * <p>🔴 <b>Warn, never block.</b> Archiving is available on ANY status and with
 * any number of live submissions. The count of open submissions is stated so
 * the decision is informed, and then the button archives anyway — a buyer whose
 * submissions are still live is a normal thing to archive when the buyer has
 * gone quiet, and refusing would leave exactly those records cluttering the
 * list forever.</p>
 *
 * <p>The "also set Not qualified" checkbox is PRE-CHECKED while the buyer is
 * still on the active ladder and absent once they are off it: rewriting an
 * existing terminal status would erase the specific reason a buyer died and
 * replace it with a generic one.</p>
 */
export function ArchiveBuyerModal({
  buyer,
  mode,
  onClose,
}: {
  buyer: BuyerLeadDetailDto;
  mode: "archive" | "unarchive";
  onClose: () => void;
}) {
  const archiving = mode === "archive";
  const qc = useQueryClient();
  const toast = useToast();

  const [reason, setReason] = useState("");
  // Pre-checked, and only offered, while the buyer is still on the active
  // ladder. `OFF_LADDER` is duplicated from buyerPhases rather than imported so
  // this file does not depend on the ladder module for a three-value test.
  const onActiveLadder =
    buyer.status !== "NotQualified" && buyer.status !== "Nurture" && buyer.status !== "Dead";
  const [alsoNotQualified, setAlsoNotQualified] = useState(onActiveLadder);

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= MIN_ARCHIVE_REASON;

  const mutation = useMutation({
    mutationFn: () =>
      archiving
        ? buyersApi.archive(buyer.id, {
            reason: trimmed,
            alsoSetNotQualified: onActiveLadder && alsoNotQualified,
          })
        : buyersApi.unarchive(buyer.id, { reason: trimmed }),
    onSuccess: () => {
      // The buyer prefix covers the detail, the list and the stat tiles.
      qc.invalidateQueries({ queryKey: buyerKeys.all });
      // The assignment queue and the specialist-load table read buyers through
      // their own keys, and an archive changes what both show.
      qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      // 🔴 And the allocation prefix: archiving FREES the specialist's capacity,
      // so their entitlement Gap moves the moment this lands. Without this the
      // manager's standing table would keep showing the old carry for up to the
      // five-minute staleTime.
      qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
      toast.success(archiving ? "Buyer archived." : "Buyer unarchived.");
      onClose();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  const openSubmissions = buyer.submissionCount ?? 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={archiving ? `Archive ${buyer.displayName}` : `Unarchive ${buyer.displayName}`}
      size="md"
    >
      <div className="grid gap-space-4">
        <p className="text-small text-gray-mid m-0">
          {archiving ? (
            <>
              They will drop out of the buyers list, the assignment queue, the buyer
              pickers and the dashboard counts. Nothing is deleted: this record still
              opens, stays editable, and keeps its place on every listing it has been
              presented to.
            </>
          ) : (
            <>
              They will return to the buyers list and the assignment queue. Their status
              is not changed back — set it deliberately if it needs to move.
            </>
          )}
        </p>

        {/* Warn, do not block. The button below is not gated on this. */}
        {archiving && openSubmissions > 0 && (
          <div
            className="rounded-md border border-status-warning bg-white-warm p-space-3"
            data-testid="archive-submissions-warning"
          >
            <p className="text-small text-onyx m-0">
              This buyer is on {openSubmissions} submission{openSubmissions === 1 ? "" : "s"}.
              Archiving does not cancel or change {openSubmissions === 1 ? "it" : "them"}, and
              they stay visible on the listings they were presented to.
            </p>
          </div>
        )}

        <label className="grid gap-space-1">
          <span className="text-label uppercase tracking-wide text-gray-mid">
            Why {archiving ? "archive" : "unarchive"} this buyer?
          </span>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="archive-reason"
            placeholder={
              archiving
                ? "e.g. Went quiet after three attempts, no longer looking."
                : "e.g. Came back in touch, ready to look again."
            }
          />
          <span className="text-caption text-gray-mid">
            At least {MIN_ARCHIVE_REASON} characters. It shows on the record, so write it
            for whoever finds this next.
          </span>
        </label>

        {archiving && onActiveLadder && (
          <label className="flex items-start gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={alsoNotQualified}
              onChange={(e) => setAlsoNotQualified(e.target.checked)}
              data-testid="archive-also-not-qualified"
              className="mt-[3px]"
            />
            <span>
              Also set their status to <strong>Not qualified</strong>. Optional — a buyer
              can be archived on any status.
            </span>
          </label>
        )}

        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={!reasonOk || mutation.isPending}
            data-testid="archive-confirm"
          >
            {mutation.isPending
              ? archiving
                ? "Archiving…"
                : "Unarchiving…"
              : archiving
                ? "Archive buyer"
                : "Unarchive buyer"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
